import { useCallback, useEffect, useReducer } from 'react';
import { VIEW_SIGNUP } from '@cx-web/feature-auth-ui/store';
import { useCxRuntime } from '@cx-web/feature-cx-runtime';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
  TRACKING_LABELS,
} from '@cx-web/feature-tracking/constants';

import onboardingReducer from './reducers';
import {
  HANDLE_INPUT_BLUR,
  HANDLE_INPUT_CHANGE,
  type OnboardingFetcher,
  type OnboardingProps,
  onboardingVariants,
  onboardingView,
  TOGGLE_AGREE_TERMS,
  UPDATE_SUBMIT_STATE,
} from './types';

const promisifiedDelay = (timeInMs: number) => new Promise(resolve => setTimeout(resolve, timeInMs));

export function useOnboardingActions(
  { variant, view = onboardingView.SECONDARY }: Pick<OnboardingProps, 'variant' | 'view'>,
  fetcher: OnboardingFetcher,
) {
  // NGF-6334: read the framework seams through the CX runtime adapter. RR7 supplies these from its
  // provider tree; atlas-astro from the island adapter mounted via withCxRuntime.
  const runtime = useCxRuntime();
  const { openAtView } = runtime.useAuthModal();
  const { track } = runtime.useTracking();

  // UI vars. These are driven purely by props (view/variant) — the mobile/desktop split that used to
  // live here is now CSS (media queries in Onboarding.module.css + a <picture> for the background
  // image), so the shared component needs no JS media hook and renders identically on first paint
  // across both SSR frameworks.

  const isSecondaryView = view === onboardingView.SECONDARY;
  const shouldRenderAsSecondary = isSecondaryView;
  const shouldRenderAsMyOCLoginCTA = isSecondaryView && variant === onboardingVariants.USER_SIGNUP;

  // State

  const [{ agreeTerms, errorOnBlur, input, submitted }, dispatch] = useReducer(onboardingReducer, {
    agreeTerms: false,
    errorOnBlur: false,
    input: { isValid: false, value: '' },
    submitted: false,
  });

  const inputIsValid = input.isValid;
  const canSubmit = shouldRenderAsMyOCLoginCTA ? inputIsValid : inputIsValid && agreeTerms;
  // Gate on BOTH fetcher in-flight states so one body serves both apps: RR7's react-router fetcher
  // emits 'loading' during the post-action revalidation, while atlas-astro's native-fetch fetcher
  // emits 'submitting' for the whole request and never 'loading' (see useAstroNewsletterFetcher).
  // Either way the button is disabled while the request is in flight, closing the double-submit window.
  const disabled = !canSubmit || fetcher.state === 'loading' || fetcher.state === 'submitting' || submitted;

  // Helpers

  const toggleTerms = useCallback(() => {
    dispatch({ type: TOGGLE_AGREE_TERMS });
  }, [dispatch]);

  const handleInputChange = useCallback(
    (value: string = '') => {
      const regex = /\S+@\S+\.\S+/;
      const isValidEmail = regex.test(value);
      const payload = {
        value,
        isValid: isValidEmail,
      };
      dispatch({ type: HANDLE_INPUT_CHANGE, payload });
    },
    [dispatch],
  );

  const handleInputBlur = useCallback(() => {
    dispatch({ type: HANDLE_INPUT_BLUR });
  }, [dispatch]);

  const updateSubmitState = useCallback(
    (state: boolean) => {
      dispatch({ type: UPDATE_SUBMIT_STATE, submit: state });
    },
    [dispatch],
  );

  const handleFormSubmit = useCallback(
    async (email: string) => {
      track(TRACKING_EVENTS.SIGN_UP_CONTAINER, {
        eventAction: TRACKING_ACTIONS.signupContainer,
        eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
        eventLabel: TRACKING_LABELS.signupContainer,
      });

      fetcher.submit({ email }, { method: 'POST', action: '/api/cx/myoc/newsletter' });
    },
    [fetcher, track],
  );

  useEffect(() => {
    const { data, state } = fetcher;
    if (state !== 'idle' || !data) return;

    if (data.success) {
      updateSubmitState(true);
      // Cancel the 5s reset on unmount so we don't dispatch into a torn-down instance (a no-op in
      // React 19, but it also avoids StrictMode's double-invoke clearing a remounted input).
      let cancelled = false;
      promisifiedDelay(5000).then(() => {
        if (cancelled) return;
        handleInputChange('');
        updateSubmitState(false);
      });
      return () => {
        cancelled = true;
      };
    }

    console.log('Onboarding error: ', data.fieldErrors);
    updateSubmitState(false);
  }, [fetcher, handleInputChange, updateSubmitState]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (shouldRenderAsMyOCLoginCTA) {
      // Open the signup view through the framework-agnostic auth-modal seam (NGF-6331). The adapter
      // routes this to the OC widget: RR7 via useAuthModal().openAtView, atlas-astro via the published
      // store. The seam is the modal's general openAtView; this caller chooses the signup view.
      openAtView(VIEW_SIGNUP, { prefillEmail: input.value });
      return;
    }

    handleFormSubmit(input.value);
  }, [handleFormSubmit, input.value, openAtView, shouldRenderAsMyOCLoginCTA]);

  return {
    agreeTerms,
    disabled,
    errorOnBlur,
    input,
    submitted,
    inputIsValid,
    canSubmit,
    fetcher,
    shouldRenderAsSecondary,
    shouldRenderAsMyOCLoginCTA,
    toggleTerms,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
  };
}
